import {
  Divider,
  Grid,
  Stack,
  useBreakpointValue,
} from '@wegroup/design-system';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import IllustrationIconSelector from '../../../common/src/components/IllustrationIconSelector';
import IllustrationRadioButton from '../../../common/src/components/IllustrationRadioButton';
import AssistantQuestion from '../components/flow/AssistantQuestion';
import ErrorPage from '../../../common/src/components/ErrorPage';
import { useLouiseDispatch } from '../store/redux/store';
import { loadSelectionState } from '../store/redux/slices/selectionSlice';
import useIsAboveViewportHeight from '../../../common/src/hooks/useIsAboveViewportHeight';
import { useFlowSessionObject } from '../hooks/useFlowQueries';
import { markFlowAsComplete } from '../services/louiseApiService';
import LoadingSpinner from '../../../common/src/components/LoadingSpinner';
import QuestionGroupActions from '../../../flows/src/components/ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import useSessionId from '../../../flows/src/hooks/useSessionId';
import ViewHeader from '../components/legacy/question-view-header/QuestionViewHeader';
import ScrollableContentContainer from '../components/layout/ScrollableContentContainer';
import useTracking from '../../../common/src/hooks/tracking/useTracking';
import { useFlowsSelector } from '../../../common/src/utils/globalVariables';
import { useCreateRecommendation } from '../../../conversations/src/features/detail/shared/api/createRecommendations';
import {
  flowIdBikmoOfferFlow,
  flowIdCyclingVlaanderenOfferFlowId,
} from '../../../flows/src/constants/flowIds';

enum NextView {
  PROSPECT,
  OFFER,
  NONE,
}

const QuestionFinalizationView: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const history = useHistory();
  const sessionId = useSessionId();
  const { mutateAsync: createRecommendation } = useCreateRecommendation();
  const flowId = useFlowsSelector((state) => state.flows.metaData.flowId);

  const appDispatch = useLouiseDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [nextView, setNextView] = useState(NextView.NONE);

  const isMobile = useBreakpointValue({ base: true, laptop: false });
  const shouldShowDivider = useBreakpointValue({ base: true, mobileL: false });

  const above480px = useIsAboveViewportHeight('vga');

  useEffect(() => {
    initial();
  }, []);

  const {
    data: sessionState,
    isLoading: sessionStateLoading,
    isError,
  } = useFlowSessionObject(sessionId);

  const initial = async () => {
    // Load the existing redux state if it is non existant
    if (sessionId) {
      await appDispatch(loadSelectionState(sessionId));
    }
    const recommendationResponse = await createRecommendation(sessionId);
    await markFlowAsComplete({
      conversationId: sessionId,
      recommendationId: recommendationResponse.id,
    });
    trackEvent('Finalize Sales Conversation');
    setIsLoading(false);
  };

  const goBack = () => {
    history.push(`/flows/${flowId}/questions/${sessionId}`);
  };

  const hasMoreThanOneRiskObject = () => {
    const riskObjects = sessionState?.risk_objects_revisions.map(
      ({ type }) => type,
    );
    if (!riskObjects) return false;

    if (riskObjects.length === new Set(riskObjects).size) return false;
    return true;
  };

  const onNext = () => {
    if (nextView === NextView.NONE) return;
    else if (nextView === NextView.OFFER) {
      trackEvent('End Sales Conversation and View Offers');
      if (
        hasMoreThanOneRiskObject() &&
        flowId !== flowIdBikmoOfferFlow &&
        flowId !== flowIdCyclingVlaanderenOfferFlowId
      ) {
        history.push(
          `/product/session/risk-object-selection?session_id=${sessionId}`,
        );
      } else {
        history.push(`/product/session/offers?session_id=${sessionId}`);
      }
    } else if (nextView === NextView.PROSPECT) {
      trackEvent('End Sales Conversation and View Party');
      history.push(`/sales-management/prospects/${sessionState?.prospect.id}`);
    }
  };

  if (isLoading || sessionStateLoading) return <LoadingSpinner />;

  if (isError) return <ErrorPage />;

  return (
    <Grid
      height="100%"
      width="100%"
      templateRows={{
        base: above480px ? 'auto minmax(1px, 1fr) auto' : 'auto auto auto',
        laptop: 'minmax(1px, 1fr) auto',
      }}
      background="#fdfcff"
    >
      {isMobile && <ViewHeader />}
      <ScrollableContentContainer>
        <AssistantQuestion
          question={t("We're all done! How would you like to continue?")}
        />
        <Stack
          spacing={{ base: '4', mobileL: '10' }}
          direction={{ base: 'column', tablet: 'row' }}
          {...(shouldShowDivider && {
            divider: <Divider color="gray.300" />,
          })}
          alignSelf="center"
        >
          <IllustrationRadioButton
            Illustration={
              <IllustrationIconSelector
                active={nextView === NextView.PROSPECT}
                type="prospect_view"
              />
            }
            text={
              <p
                dangerouslySetInnerHTML={{
                  __html: t('End the flow and view the prospect'),
                }}
              />
            }
            description={t(
              'Get back to calculating offers at a later time and view the prospect’s information in your portfolio',
            )}
            isSelected={nextView === NextView.PROSPECT}
            onClick={() => setNextView(NextView.PROSPECT)}
          />

          <IllustrationRadioButton
            Illustration={
              <IllustrationIconSelector
                active={nextView === NextView.OFFER}
                type="offer_view"
              />
            }
            text={
              <p
                dangerouslySetInnerHTML={{
                  __html: t('Continue and start calculating offers'),
                }}
              ></p>
            }
            description={t(
              'Continue using the data provided and calculate the quotes using this data',
            )}
            isSelected={nextView === NextView.OFFER}
            onClick={() => setNextView(NextView.OFFER)}
            dataTestId="ADVISORY-CONVO_FINALIZATION_ACTIONS_VIEW-OFFERS"
          />
        </Stack>
      </ScrollableContentContainer>
      <QuestionGroupActions
        onBack={goBack}
        onContinue={onNext}
        disabledContinue={nextView === NextView.NONE}
        noSkip
      />
    </Grid>
  );
};

export default QuestionFinalizationView;
